import React, { useState, useEffect } from 'react'
import AppLayout from '../components/AppLayout'
import { useAuth } from '../contexts/AuthContext'
import { supabase, ugandaDate, ugandaMonth, ugandaDatetime } from '../lib/supabase'
import { journalSupplierPayment } from '../lib/journalEngine'

export default function Payables() {
  const { user, profile, isAdmin } = useAuth()
  const [payables, setPayables] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(null)
  const [msg, setMsg] = useState(null)
  const [tab, setTab] = useState('due')
  const [showAddModal, setShowAddModal] = useState(false)
  const [grns, setGrns] = useState([])
  const [pos, setPOs] = useState([])

  const emptyForm = { supplier:'', invoice_number:'', invoice_date: ugandaDate(),
    payment_terms:30, amount:'', grn_id:'', po_id:'', notes:'' }
  const [form, setForm] = useState(emptyForm)

  useEffect(() => { loadData() }, [])

  const loadData = async () => {
    setLoading(true)
    const [payRes, grnRes, poRes] = await Promise.all([
      supabase.from('supplier_payables').select('*'),
      supabase.from('goods_received_notes').select('id, grn_number, supplier').eq('status','Confirmed'),
      supabase.from('purchase_orders').select('id, po_number, supplier').eq('is_approved',true),
    ])
    setPayables(payRes.data || [])
    setGrns(grnRes.data || [])
    setPOs(poRes.data || [])
    setLoading(false)
  }

  const handleAddInvoice = async () => {
    if (!form.supplier || !form.amount) { setMsg({ type:'error', text:'Supplier and amount are required.' }); return }
    const invoiceDate = new Date(form.invoice_date)
    const dueDate = new Date(invoiceDate)
    dueDate.setDate(dueDate.getDate() + parseInt(form.payment_terms))
    const { error } = await supabase.from('supplier_invoices').insert({
      created_by: user.id,
      supplier: form.supplier,
      invoice_number: form.invoice_number,
      invoice_date: form.invoice_date,
      due_date: ugandaDate(dueDate),
      payment_terms: parseInt(form.payment_terms),
      amount: parseFloat(form.amount),
      balance_due: parseFloat(form.amount),
      grn_id: form.grn_id || null,
      po_id: form.po_id || null,
      notes: form.notes,
      po_matched: !!form.po_id,
      grn_matched: !!form.grn_id,
    })
    if (error) { setMsg({ type:'error', text:error.message }); return }
    setMsg({ type:'success', text:'Supplier invoice added to payables.' })
    setShowAddModal(false); setForm(emptyForm); loadData()
    setTimeout(() => setMsg(null), 3000)
  }

  const handleApprove = async (inv, person) => {
    const updates = person === 'sharon'
      ? { approved_by_sharon:true, sharon_approved_at:new Date().toISOString() }
      : { approved_by_edward:true, edward_approved_at:new Date().toISOString() }
    await supabase.from('supplier_invoices').update(updates).eq('id', inv.id)
    loadData()
  }

  const [payModal, setPayModal] = React.useState(null)
  const [payForm, setPayForm] = React.useState({ method:'Bank Transfer', ref:'' })

  const handlePay = (inv) => {
    if (!inv.ready_to_pay) { setMsg({ type:'error', text:'Cannot pay — three-way match or approvals not complete.' }); return }
    setPayModal(inv); setPayForm({ method:'Bank Transfer', ref:'' })
  }

  const handleConfirmPay = async () => {
    setSaving(payModal.id)
    const amount = parseFloat(payModal.balance_due) || 0
    const payDate = ugandaDate()

    const { data: paymentRecord } = await supabase.from('payments_out').insert({
      supplier_invoice_id: payModal.id,
      paid_by: user.id,
      amount, payment_date: payDate,
      payment_method: payForm.method,
      reference: payForm.ref,
      sharon_approved: true,
    }).select().single()

    await supabase.from('supplier_invoices').update({
      status:'Paid', amount_paid: payModal.amount, balance_due: 0,
      paid_on: payDate,
      payment_method: payForm.method, payment_ref: payForm.ref, updated_at: new Date().toISOString()
    }).eq('id', payModal.id)

    // Auto-create cash log entry (cash out)
    await supabase.from('cash_logs').insert({
      user_id: user.id,
      log_date: payDate,
      total_income: 0, total_expenses: amount, net: -amount,
      income_rows: [],
      expense_rows: [{ type: 'invoice', description: `Supplier payment — ${payModal.supplier || ''}`, amount }],
      notes: `Supplier invoice payment — ${payModal.supplier || ''} — ${payForm.ref || ''}`,
    })

    // Journal entry: Dr 2101 Supplier Payables / Cr 1101 Cash
    if (paymentRecord) {
      await journalSupplierPayment({
        paymentId: paymentRecord.id,
        supplier: payModal.supplier || '',
        amount, date: payDate,
        userId: user.id,
      })
    }

    setSaving(null); setPayModal(null); loadData()
    setMsg({ type:'success', text:`Payment of UGX ${amount.toLocaleString()} recorded. Cash balance and journal updated.` })
    setTimeout(() => setMsg(null), 3000)
  }

  const isSharonOrEdward = profile?.permission_level === 'admin'
  const isSharonEmail = profile?.email?.includes('snankunda')
  const isEdwardEmail = profile?.email?.includes('elkatongole')

  const totalDue = payables.reduce((s, p) => s + (p.balance_due||0), 0)
  const overdue = payables.filter(p => p.is_overdue)
  const dueSoon = payables.filter(p => !p.is_overdue && p.days_until_due <= 7 && p.days_until_due >= 0)

  const filtered = tab === 'due' ? payables.filter(p => !p.is_overdue && p.days_until_due <= 14)
    : tab === 'overdue' ? payables.filter(p => p.is_overdue)
    : payables

  return (
    <AppLayout title="Payables">
      <div className="section-header">
        <div className="section-icon">💸</div>
        <div className="section-info">
          <h2>Supplier Payables</h2>
          <p>All money owed to suppliers. Three-way match required before payment.</p>
        </div>
        <button className="btn btn-primary" style={{ marginLeft:'auto' }} onClick={() => setShowAddModal(true)}>+ Add Supplier Invoice</button>
      </div>

      {msg && <div className={`alert ${msg.type==='success'?'alert-green':'alert-red'}`}>{msg.text}</div>}

      {overdue.length > 0 && <div className="alert alert-red">🚨 <strong>{overdue.length} overdue payment{overdue.length>1?'s':''}</strong> — action required immediately.</div>}
      {dueSoon.length > 0 && <div className="alert alert-amber">⏰ <strong>{dueSoon.length} payment{dueSoon.length>1?'s':''}</strong> due within 7 days.</div>}

      <div className="kpi-grid" style={{ marginBottom:18 }}>
        <div className="kpi"><div className="kpi-label">Total Payables</div><div className="kpi-value" style={{ fontSize:'1.2rem' }}>UGX {totalDue.toLocaleString()}</div></div>
        <div className={`kpi ${overdue.length>0?'red':''}`}><div className="kpi-label">Overdue</div><div className="kpi-value">{overdue.length}</div></div>
        <div className={`kpi ${dueSoon.length>0?'':''}`}><div className="kpi-label">Due in 7 Days</div><div className="kpi-value">{dueSoon.length}</div></div>
        <div className="kpi green"><div className="kpi-label">Ready to Pay</div><div className="kpi-value">{payables.filter(p=>p.ready_to_pay).length}</div></div>
      </div>

      <div className="tabs">
        <button className={`tab ${tab==='due'?'active':''}`} onClick={() => setTab('due')}>📅 Due Soon</button>
        <button className={`tab ${tab==='overdue'?'active':''}`} onClick={() => setTab('overdue')}>🚨 Overdue ({overdue.length})</button>
        <button className={`tab ${tab==='all'?'active':''}`} onClick={() => setTab('all')}>📚 All</button>
      </div>

      <div className="card">
        {loading ? <div style={{ textAlign:'center', padding:32 }}><span className="spinner dark" /></div>
        : filtered.length === 0 ? <div className="empty-state"><div className="empty-icon">✅</div><h3>No payables in this view</h3></div>
        : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Supplier</th><th>Invoice #</th><th>Amount (UGX)</th><th>Due Date</th><th>Days</th><th>PO ✓</th><th>GRN ✓</th><th>Sharon ✓</th><th>Edward ✓</th><th>Status</th><th>Action</th></tr>
              </thead>
              <tbody>
                {filtered.map(inv => (
                  <tr key={inv.id} style={{ background: inv.is_overdue?'#fff5f5':inv.days_until_due<=3?'#fffbf0':undefined }}>
                    <td style={{ fontWeight:700 }}>{inv.supplier}</td>
                    <td style={{ fontFamily:"'DM Mono',monospace", fontSize:'0.78rem' }}>{inv.invoice_number||'—'}</td>
                    <td style={{ fontWeight:700 }}>UGX {(inv.balance_due||0).toLocaleString()}</td>
                    <td style={{ fontSize:'0.78rem' }}>{inv.due_date ? new Date(inv.due_date).toLocaleDateString('en-UG') : '—'}</td>
                    <td>
                      {inv.days_until_due !== null
                        ? <span style={{ fontWeight:700, color:inv.is_overdue?'var(--red)':inv.days_until_due<=7?'var(--amber)':'var(--green-mid)' }}>
                            {inv.is_overdue ? `${Math.abs(inv.days_until_due)}d overdue` : `${inv.days_until_due}d`}
                          </span>
                        : '—'}
                    </td>
                    <td>{inv.po_matched ? '✅' : <span style={{ color:'#ccc' }}>⬜</span>}</td>
                    <td>{inv.grn_matched ? '✅' : <span style={{ color:'#ccc' }}>⬜</span>}</td>
                    <td>
                      {inv.approved_by_sharon ? '✅'
                        : isSharonEmail ? <button className="btn btn-secondary btn-sm" onClick={() => handleApprove(inv,'sharon')}>Approve</button>
                        : <span style={{ color:'#ccc' }}>Pending</span>}
                    </td>
                    <td>
                      {inv.amount > 1000000
                        ? inv.approved_by_edward ? '✅'
                          : isEdwardEmail ? <button className="btn btn-secondary btn-sm" onClick={() => handleApprove(inv,'edward')}>Approve</button>
                          : <span style={{ color:'var(--amber)', fontSize:'0.72rem' }}>Required</span>
                        : <span style={{ color:'var(--text-muted)', fontSize:'0.72rem' }}>N/A</span>}
                    </td>
                    <td><span className={`badge ${inv.ready_to_pay?'badge-green':'badge-grey'}`}>{inv.ready_to_pay?'Ready':'Pending'}</span></td>
                    <td>
                      {inv.ready_to_pay && isSharonOrEdward && (
                        <button className="btn btn-primary btn-sm" onClick={() => handlePay(inv)} disabled={saving===inv.id}>
                          {saving===inv.id?<><span className="spinner" />...</>:'💳 Pay'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add Invoice Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={e => e.target===e.currentTarget && setShowAddModal(false)}>
          <div className="modal" style={{ maxWidth:540 }}>
            <div className="modal-title">+ Add Supplier Invoice</div>
            <div className="modal-subtitle">Enter details from the supplier's invoice. Link to PO and GRN for three-way match.</div>
            <div className="grid-2">
              <div className="form-group"><label className="form-label">Supplier *</label><input className="form-input" value={form.supplier} onChange={e => setForm(f=>({...f,supplier:e.target.value}))} /></div>
              <div className="form-group"><label className="form-label">Supplier Invoice #</label><input className="form-input" value={form.invoice_number} onChange={e => setForm(f=>({...f,invoice_number:e.target.value}))} /></div>
              <div className="form-group"><label className="form-label">Invoice Date</label><input className="form-input" type="date" value={form.invoice_date} onChange={e => setForm(f=>({...f,invoice_date:e.target.value}))} /></div>
              <div className="form-group"><label className="form-label">Payment Terms (days)</label>
                <select className="form-select" value={form.payment_terms} onChange={e => setForm(f=>({...f,payment_terms:e.target.value}))}>
                  {[0,7,14,30,60,90,180].map(d => <option key={d} value={d}>{d===0?'On Delivery':`Net ${d}`}</option>)}
                </select>
              </div>
              <div className="form-group"><label className="form-label">Amount (UGX) *</label><input className="form-input" type="number" value={form.amount} onChange={e => setForm(f=>({...f,amount:e.target.value}))} /></div>
            </div>
            <div className="grid-2">
              <div className="form-group"><label className="form-label">Link to PO</label>
                <select className="form-select" value={form.po_id} onChange={e => setForm(f=>({...f,po_id:e.target.value}))}>
                  <option value="">None</option>
                  {pos.map(p => <option key={p.id} value={p.id}>{p.po_number} — {p.supplier}</option>)}
                </select>
              </div>
              <div className="form-group"><label className="form-label">Link to GRN</label>
                <select className="form-select" value={form.grn_id} onChange={e => setForm(f=>({...f,grn_id:e.target.value}))}>
                  <option value="">None</option>
                  {grns.map(g => <option key={g.id} value={g.id}>{g.grn_number} — {g.supplier}</option>)}
                </select>
              </div>
            </div>
            <div className="form-group"><label className="form-label">Notes</label><textarea className="form-textarea" style={{ minHeight:50 }} value={form.notes} onChange={e => setForm(f=>({...f,notes:e.target.value}))} /></div>
            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={() => setShowAddModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleAddInvoice}>💾 Add to Payables</button>
            </div>
          </div>
        </div>
      )}

      {payModal && (
        <div className="modal-overlay" onClick={e => e.target===e.currentTarget && setPayModal(null)}>
          <div className="modal">
            <div className="modal-title">💸 Record Payment</div>
            <div className="modal-subtitle">
              <strong>{payModal.supplier}</strong><br/>
              Amount due: <strong>UGX {(payModal.balance_due||0).toLocaleString()}</strong>
            </div>
            <div className="form-group">
              <label className="form-label">Payment Method</label>
              <select className="form-select" value={payForm.method} onChange={e=>setPayForm(f=>({...f,method:e.target.value}))}>
                {['Bank Transfer','Mobile Money','Cash','Cheque'].map(m=><option key={m}>{m}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Reference / Transaction Number</label>
              <input className="form-input" value={payForm.ref} onChange={e=>setPayForm(f=>({...f,ref:e.target.value}))} placeholder="e.g. TXN-12345" />
            </div>
            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={()=>setPayModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleConfirmPay} disabled={!!saving}>
                {saving ? <><span className="spinner"/>Processing...</> : '✅ Confirm Payment'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  )
}
